import { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const INK = "#111111";
const XLSX_MIME =
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const HOW_IT_WORKS = [
	{
		n: "1",
		title: "Upload PDFs",
		body: "Drop one or more line-sheet PDFs — drag & drop or browse.",
	},
	{
		n: "2",
		title: "Read page by page",
		body: "Every page is read visually, style by style, with a live progress bar.",
	},
	{
		n: "3",
		title: "Download Excel",
		body: "A grouped spreadsheet with product images and auto-detected attributes.",
	},
];

const formatKb = (bytes) => {
	const kb = (bytes || 0) / 1024;
	if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`;
	return `${Math.max(1, Math.round(kb))} KB`;
};

const base64ToXlsxBlob = (b64) => {
	const bin = atob(b64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
	return new Blob([bytes], { type: XLSX_MIME });
};

const PdfToExcel = () => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [done, setDone] = useState(false);
	const [dragging, setDragging] = useState(false);
	const [progress, setProgress] = useState({ current: 0, total: 0, message: "" });
	const inputRef = useRef(null);

	useEffect(() => {
		if (!loading) return undefined;
		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [loading]);

	const totalKb = useMemo(
		() => files.reduce((acc, f) => acc + (f.size || 0), 0),
		[files]
	);

	const acceptFiles = (incoming) => {
		setError("");
		setDone(false);
		const list = Array.from(incoming || []);
		if (!list.length) return;
		const nonPdf = list.filter(
			(f) => !String(f.name || "").toLowerCase().endsWith(".pdf")
		);
		if (nonPdf.length) {
			setError("Only PDF files (.pdf) are allowed.");
			return;
		}
		// De-duplicate by name + size, append to any existing selection.
		setFiles((prev) => {
			const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
			const merged = [...prev];
			list.forEach((f) => {
				const key = `${f.name}:${f.size}`;
				if (!seen.has(key)) {
					seen.add(key);
					merged.push(f);
				}
			});
			return merged;
		});
	};

	const onPickFiles = (e) => {
		acceptFiles(e.target.files);
		if (inputRef.current) inputRef.current.value = "";
	};

	const removeFile = (idx) => {
		setError("");
		setDone(false);
		setFiles((prev) => prev.filter((_, i) => i !== idx));
	};

	const clearFiles = () => {
		setFiles([]);
		setError("");
		setDone(false);
	};

	const onDrop = (e) => {
		e.preventDefault();
		setDragging(false);
		if (loading) return;
		acceptFiles(e.dataTransfer?.files);
	};

	const downloadBlob = (blob, filename) => {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
	};

	const convert = async () => {
		if (!files.length) {
			setError("Select at least one PDF");
			return;
		}
		setLoading(true);
		setError("");
		setDone(false);
		setProgress({ current: 0, total: 0, message: "Uploading…" });
		try {
			const form = new FormData();
			files.forEach((f) => form.append("files", f));

			const base = (process.env.REACT_APP_PYTHON_APP_API_URL || "").replace(/\/+$/, "");
			const token = localStorage.getItem("py_token");
			const resp = await fetch(`${base}/reports/pdf-to-excel/ai`, {
				method: "POST",
				headers: token ? { Authorization: `Bearer ${token}` } : {},
				body: form,
			});

			if (resp.status === 401) {
				localStorage.removeItem("user_token");
				localStorage.removeItem("py_token");
				window.location.href = "/";
				return;
			}
			if (!resp.ok || !resp.body) {
				let msg = `Conversion failed (${resp.status})`;
				try {
					const j = await resp.json();
					msg = j?.message || j?.error || msg;
				} catch (parseErr) {
					/* keep default message */
				}
				throw new Error(msg);
			}

			// Stream the NDJSON progress + final file.
			const reader = resp.body.getReader();
			const decoder = new TextDecoder();
			let buffer = "";
			let result = null;
			let chunk = await reader.read();
			while (!chunk.done) {
				buffer += decoder.decode(chunk.value, { stream: true });
				let nl = buffer.indexOf("\n");
				while (nl >= 0) {
					const line = buffer.slice(0, nl).trim();
					buffer = buffer.slice(nl + 1);
					if (line) {
						let evt = null;
						try {
							evt = JSON.parse(line);
						} catch (jsonErr) {
							evt = null;
						}
						if (evt && evt.type === "progress") {
							setProgress({
								current: evt.current || 0,
								total: evt.total || 0,
								message: evt.message || "",
							});
						} else if (evt && evt.type === "done") {
							result = evt;
						} else if (evt && evt.type === "error") {
							throw new Error(evt.message || "Conversion failed");
						}
					}
					nl = buffer.indexOf("\n");
				}
				chunk = await reader.read();
			}

			if (!result) throw new Error("Conversion ended unexpectedly.");
			downloadBlob(base64ToXlsxBlob(result.data), result.filename || "line_sheet.xlsx");
			setDone(true);
		} catch (err) {
			setError(err?.message || "Conversion failed");
		} finally {
			setLoading(false);
		}
	};

	const hasFiles = files.length > 0;
	const pct = progress.total
		? Math.min(100, Math.round((progress.current / progress.total) * 100))
		: 0;

	return (
		<Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 920, mx: "auto" }}>
			<Paper
				elevation={0}
				sx={{
					borderRadius: 3,
					overflow: "hidden",
					border: (t) => `1px solid ${t.palette.divider}`,
				}}>
				{/* Branded header */}
				<Box sx={{ bgcolor: INK, color: "#fff", px: { xs: 2.5, md: 3.5 }, py: 3 }}>
					<Typography
						sx={{ letterSpacing: 6, fontWeight: 700, fontSize: 12, opacity: 0.85 }}>
						RAREISM
					</Typography>
					<Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
						Line Sheet Convertor
					</Typography>
					<Typography variant="body2" sx={{ opacity: 0.7, mt: 0.75, maxWidth: 640 }}>
						Turn line-sheet PDFs into a clean, image-rich Excel — every style with its
						attributes, grouped by category, with product images embedded.
					</Typography>
					<Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
						{["Auto-detects fields", "Groups by category", "Embeds product images"].map(
							(label) => (
								<Chip
									key={label}
									label={label}
									size="small"
									sx={{
										bgcolor: "rgba(255,255,255,0.12)",
										color: "#fff",
										fontWeight: 500,
										borderRadius: 1,
									}}
								/>
							)
						)}
					</Stack>
				</Box>

				<Box sx={{ p: { xs: 2, md: 3.5 } }}>
					{/* Drag & drop upload zone */}
					<Box
						role="button"
						tabIndex={0}
						onClick={() => !loading && inputRef.current?.click()}
						onKeyDown={(e) => {
							if ((e.key === "Enter" || e.key === " ") && !loading)
								inputRef.current?.click();
						}}
						onDragOver={(e) => {
							e.preventDefault();
							if (!loading) setDragging(true);
						}}
						onDragLeave={() => setDragging(false)}
						onDrop={onDrop}
						sx={{
							cursor: loading ? "not-allowed" : "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: { xs: 1.5, sm: 2.5 },
							flexWrap: "wrap",
							textAlign: { xs: "center", sm: "left" },
							px: 2.5,
							py: 2.5,
							borderRadius: 2.5,
							border: "2px dashed",
							borderColor: dragging ? INK : "divider",
							bgcolor: dragging ? "rgba(17,17,17,0.04)" : "#fafafa",
							transition: "all .15s ease",
							outline: "none",
							"&:hover": { borderColor: loading ? "divider" : "#9e9e9e", bgcolor: "#f5f5f5" },
						}}>
						<CloudUploadRoundedIcon
							sx={{ fontSize: 36, color: dragging ? INK : "text.disabled" }}
						/>
						<Box>
							<Typography sx={{ fontWeight: 600, lineHeight: 1.3 }}>
								{dragging ? "Drop to add files" : "Drag & drop your line-sheet PDFs"}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								or click anywhere here · multiple PDFs supported
							</Typography>
						</Box>
						<Box
							component="span"
							sx={{
								px: 2,
								py: 0.75,
								borderRadius: 1.5,
								border: "1px solid",
								borderColor: "#bdbdbd",
								fontSize: 13,
								fontWeight: 600,
								color: INK,
								whiteSpace: "nowrap",
							}}>
							Browse files
						</Box>
						<input
							ref={inputRef}
							type="file"
							hidden
							accept="application/pdf"
							multiple
							onChange={onPickFiles}
						/>
					</Box>

					{/* Selected files */}
					{hasFiles && (
						<Box sx={{ mt: 2 }}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								sx={{ mb: 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
									{files.length} PDF{files.length > 1 ? "s" : ""} selected
									<Typography component="span" color="text.secondary" sx={{ fontWeight: 400 }}>
										{"  ·  "}
										{formatKb(totalKb)} total
									</Typography>
								</Typography>
								<Button
									size="small"
									color="inherit"
									onClick={clearFiles}
									disabled={loading}
									sx={{ textTransform: "none", color: "text.secondary" }}>
									Clear all
								</Button>
							</Stack>
							<Stack spacing={1}>
								{files.map((f, idx) => (
									<Stack
										key={`${f.name}-${f.size}-${idx}`}
										direction="row"
										alignItems="center"
										spacing={1.5}
										sx={{
											px: 1.5,
											py: 1,
											borderRadius: 2,
											border: (t) => `1px solid ${t.palette.divider}`,
											bgcolor: "#fff",
										}}>
										<PictureAsPdfRoundedIcon sx={{ color: "#c0392b" }} />
										<Box sx={{ minWidth: 0, flex: 1 }}>
											<Typography
												variant="body2"
												noWrap
												title={f.name}
												sx={{ fontWeight: 500 }}>
												{f.name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{formatKb(f.size)}
											</Typography>
										</Box>
										<IconButton
											size="small"
											onClick={() => removeFile(idx)}
											disabled={loading}
											aria-label={`Remove ${f.name}`}>
											<CloseRoundedIcon fontSize="small" />
										</IconButton>
									</Stack>
								))}
							</Stack>
						</Box>
					)}

					<Divider sx={{ my: 2.5 }} />

					{/* Convert action */}
					<Button
						fullWidth
						variant="contained"
						onClick={convert}
						disabled={loading || !hasFiles}
						startIcon={
							loading ? (
								<CircularProgress size={18} sx={{ color: "inherit" }} />
							) : (
								<AutoAwesomeRoundedIcon />
							)
						}
						sx={{
							bgcolor: INK,
							textTransform: "none",
							fontWeight: 600,
							py: 1.25,
							fontSize: 15,
							"&:hover": { bgcolor: "#000" },
						}}>
						{loading ? "Converting…" : "Convert"}
					</Button>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
						Reads each page visually for best accuracy — picks the product sketch over the
						model photo and groups styles by category.
					</Typography>

					{/* Live progress */}
					{loading && (
						<Box sx={{ mt: 2 }}>
							<Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
								<Typography variant="caption" color="text.secondary">
									{progress.message || "Working…"}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{progress.total ? `${pct}%` : ""}
								</Typography>
							</Stack>
							<LinearProgress
								variant={progress.total ? "determinate" : "indeterminate"}
								value={pct}
								sx={{
									borderRadius: 1,
									height: 8,
									bgcolor: "rgba(17,17,17,0.08)",
									"& .MuiLinearProgress-bar": { bgcolor: INK },
								}}
							/>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
								{progress.total
									? `${progress.current} / ${progress.total} pages · please don’t close or refresh`
									: "Please don’t close or refresh"}
							</Typography>
						</Box>
					)}

					{!loading && done && (
						<Alert
							icon={<CheckCircleRoundedIcon fontSize="inherit" />}
							severity="success"
							sx={{ mt: 2, borderRadius: 2 }}>
							Done — your Excel has been downloaded.
						</Alert>
					)}

					{!loading && error && (
						<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
							{error}
						</Alert>
					)}

					<Divider sx={{ my: 3 }} />

					{/* How it works — fills the lower area */}
					<Typography
						sx={{
							fontSize: 11,
							letterSpacing: 2,
							fontWeight: 700,
							color: "text.secondary",
							mb: 2,
						}}>
						HOW IT WORKS
					</Typography>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
							gap: 2,
						}}>
						{HOW_IT_WORKS.map((step) => (
							<Box
								key={step.n}
								sx={{
									p: 2,
									borderRadius: 2.5,
									border: (t) => `1px solid ${t.palette.divider}`,
									bgcolor: "#fafafa",
								}}>
								<Box
									sx={{
										width: 28,
										height: 28,
										borderRadius: "50%",
										bgcolor: INK,
										color: "#fff",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										fontSize: 13,
										fontWeight: 700,
										mb: 1,
									}}>
									{step.n}
								</Box>
								<Typography sx={{ fontWeight: 700, fontSize: 14 }}>
									{step.title}
								</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.5 }}>
									{step.body}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>
			</Paper>
		</Box>
	);
};

export default PdfToExcel;
